'use client';

import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import ImageModal from '@wt/game/modals/ImageModal';
import ImageModalToggle from '@wt/game/modals/ImageModal/toggle';
import { useBannerAdOpen } from '@wt/game/providers/bannerAdOpen/BannerAdOpenProvider';
import MapElement from '@wt/shared/components/maps/mapkit/map';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { isYearInValidRange } from '@wt/shared/components/yearslider/utils';
import MapkitAutocomplete from '@wt/shared/libs/mapkit/components/MapkitAutocomplete';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit/events';
import { mapkitSearch } from '@wt/shared/libs/mapkit/util/mapkitSearch';
import useDebounce from '@wt/utilities/useDebounce';
import { isEmpty, isNil } from 'lodash';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { CompletedIcon } from './CompletedIcon';
import { useGameFormProvider } from './GameFormProvider';
import { GameRound } from './types';
import {
  MessageHolderType,
  useCustomMessageHolder,
} from './useCustomMessageHolder';
import { getImageMetadata } from './utils';
import { getSiteMode } from '@wt/utilities/siteMode';
import MapModal from '@wt/game/modals/MapModal';
import { smallButtonClasses } from '@wt/shared/components/buttons/SmallButton';

export const RoundInputs = ({
  index,
  round,
}: {
  index: number;
  round: GameRound;
}) => {
  const { setRoundData } = useGameFormProvider();
  const { adOpen } = useBannerAdOpen();
  const imageMessageHolder = useCustomMessageHolder();
  const mapMessageHolder = useCustomMessageHolder();
  const yearMessageHolder = useCustomMessageHolder();

  const top = useMemo(() => {
    return adOpen
      ? 'top-[145px] md:top-[162px] lg:top-[220px]'
      : 'top-[105px] md:top-[130px] lg:top-[133px]';
  }, [adOpen]);

  return (
    <div className="flex w-full flex-col items-center border-b border-secondary">
      <div
        className={`sticky ${top} z-20 flex w-full flex-row justify-between bg-base-100 py-3 hover:cursor-pointer`}
        onClick={() => setRoundData({ isOpen: !round.isOpen }, index)}
      >
        <RoundTag index={index} dataValid={round.valid} />
        <ViewDetailsOrCollapseButton isOpen={round.isOpen} />
      </div>
      <div
        className={`${round.isOpen ? 'mt-4' : 'mt-0'} flex w-full flex-col gap-3 overflow-hidden transition-all duration-500 ease-in-out ${round.isOpen ? 'max-h-[5000px]' : 'max-h-0'} md:flex-row`}
      >
        <div className="flex w-full flex-col gap-3 md:w-1/2">
          <ImageInput
            index={index}
            image={round.image}
            loadingMetadata={round.loadingMetadata}
            imageMessageHolder={imageMessageHolder}
            mapMessageHolder={mapMessageHolder}
            yearMessageHolder={yearMessageHolder}
          />
          <LocationInput
            index={index}
            location={round.location}
            mapMessageHolder={mapMessageHolder}
          />
        </div>
        <div className="flex w-full flex-col gap-3 md:w-1/2">
          {getSiteMode() != 'wheretaken' && (
            <YearInput
              index={index}
              year={round.year}
              yearMessageHolder={yearMessageHolder}
            />
          )}
          <DescriptionInput index={index} description={round.description} />
          <PhotographerInput index={index} photographer={round.photographer} />
        </div>
      </div>
    </div>
  );
};

const ImageInput = ({
  index,
  image,
  loadingMetadata,
  imageMessageHolder,
  mapMessageHolder,
  yearMessageHolder,
}: {
  index: number;
  image: File | string | null;
  loadingMetadata: boolean;
  imageMessageHolder: MessageHolderType;
  mapMessageHolder: MessageHolderType;
  yearMessageHolder: MessageHolderType;
}) => {
  const { setRoundData, loading } = useGameFormProvider();
  const src = image instanceof File ? URL.createObjectURL(image) : image;

  const getMetadataAndSetInfo = async (image: File): Promise<void> => {
    setRoundData({ loadingMetadata: true }, index);

    const update: Partial<GameRound> = {
      image,
      loadingMetadata: false,
    };
    const metadata = await getImageMetadata(image);
    if (metadata.location) {
      update.location = metadata.location;
      mapMessageHolder.setMessage({
        message: 'Location determined successfully.',
        type: 'success',
      });
    }
    if (metadata.year) {
      update.year = metadata.year;
      yearMessageHolder.setMessage({
        message: 'Year determined successfully.',
        type: 'success',
      });
    }

    setRoundData(update, index);
    return;
  };

  const borderColor =
    imageMessageHolder.messageHolderProps.messageVisible &&
    imageMessageHolder.messageHolderProps.message &&
    imageMessageHolder.messageHolderProps.message.type === 'error'
      ? 'border-red-500'
      : 'border-[#645C70]';

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row justify-between">
        <span className="mb-1 text-sm font-medium">Image</span>
        <CompletedIcon completed={!isNil(image)} />
      </div>
      <div
        className={`relative flex aspect-[5/3] w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed ${borderColor} bg-secondary`}
      >
        {loadingMetadata ? (
          <Spinner />
        ) : isNil(image) || isNil(src) ? (
          <div className="mb-5 text-6xl text-highlight">
            <AddAPhotoOutlinedIcon fontSize="inherit" />
          </div>
        ) : (
          <img
            src={src}
            alt="Round Picture"
            width="100%"
            height="100%"
            className="size-full object-cover object-center"
          />
        )}
        {!isNil(image) && !loadingMetadata && (
          <ImageModalToggle
            htmlFor={`round_${index}_image_modal`}
            className={`absolute right-4 top-4 z-10 ${smallButtonClasses}`}
          >
            <FullscreenRoundedIcon htmlColor="black" />
          </ImageModalToggle>
        )}
        {!loading && !loadingMetadata && (
          <label
            htmlFor={`round_${index}_image_input`}
            className="absolute bottom-4 left-1/2 z-10 mt-2 flex h-[33px] min-h-[33px] w-fit shrink -translate-x-1/2 cursor-pointer select-none items-center rounded-lg border border-gray-400 bg-white px-3 text-base font-medium text-[#230351] hover:hover:bg-gray-100"
          >
            <FileUploadOutlinedIcon className="pr-1" />
            {`Upload${!isNil(image) ? ' new' : ''}`}
          </label>
        )}
        <imageMessageHolder.MessageHolder
          {...imageMessageHolder.messageHolderProps}
        />
      </div>
      <input
        type="file"
        accept="image/*"
        id={`round_${index}_image_input`}
        className="hidden"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (e.target?.files && e.target.files.length) {
            const file = e.target.files[0];
            if (file.type === 'image/heic') {
              e.target.value = '';
              imageMessageHolder.setMessage({
                message: 'Unsupported file type.',
                type: 'error',
              });
              return;
            }
            // Check if the file is larger than 10MB
            const fileSize = file.size / 1024 / 1024;
            if (fileSize > 10) {
              e.target.value = '';
              imageMessageHolder.setMessage({
                message: 'Please select an image smaller than 10MB.',
                type: 'error',
              });
            } else {
              getMetadataAndSetInfo(file);
            }
          }
        }}
      />
      {!isNil(image) && !isNil(src) && (
        <ImageModal id={`round_${index}_image_modal`} src={src} alt={''} />
      )}
    </div>
  );
};

const LocationInput = ({
  index,
  location,
  mapMessageHolder,
}: {
  index: number;
  location: mapkit.Coordinate | null;
  mapMessageHolder: MessageHolderType;
}) => {
  const [mapSearchInput, setMapSearchInput] = useState<string>('');
  const debouncedSearchInput = useDebounce(mapSearchInput, 1000);
  const [searchResults, setSearchResults] = useState<
    mapkit.SearchAutocompleteResult[]
  >([]);
  const { setRoundData, loading } = useGameFormProvider();

  const handleChangeLocation = (e: MapInteractionEvent | mapkit.Coordinate) => {
    if (loading) return;
    if (e instanceof mapkit.Coordinate) {
      setRoundData({ location: e }, index);
      clearSearch();
      return;
    }
    const coords = e.toCoordinates();
    if (coords && coords instanceof mapkit.Coordinate) {
      setRoundData({ location: coords }, index);
      clearSearch();
    }
  };

  const clearSearch = () => {
    setMapSearchInput('');
    setSearchResults([]);
    return;
  };

  const borderColor =
    mapMessageHolder.messageHolderProps.messageVisible &&
    mapMessageHolder.messageHolderProps.message &&
    mapMessageHolder.messageHolderProps.message.type === 'success'
      ? 'border-[#0AC265]'
      : 'border-[#645C70]';

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row justify-between">
        <span className="mb-1 text-sm font-medium">Location</span>
        <CompletedIcon completed={!isNil(location)} />
      </div>
      <div
        className={`relative flex aspect-[5/3] w-full items-center justify-center overflow-hidden rounded-2xl border ${borderColor}`}
      >
        <NewGameMapElement
          selectedLocation={location ?? undefined}
          handleChangeLocation={handleChangeLocation}
          index={index}
          mapSearchInput={mapSearchInput}
          setMapSearchInput={setMapSearchInput}
          debouncedSearchInput={debouncedSearchInput}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
        />
        <mapMessageHolder.MessageHolder
          {...mapMessageHolder.messageHolderProps}
        />
      </div>
      <MapModal
        id={`round_${index}_map_modal`}
        selectedLocation={location ?? undefined}
        onLocationSelect={handleChangeLocation}
        label={' '}
        showSearchBar={true}
        mapSearchInput={mapSearchInput}
        setMapSearchInput={setMapSearchInput}
      />
    </div>
  );
};

const NewGameMapElement = ({
  selectedLocation,
  handleChangeLocation,
  index,
  mapSearchInput,
  setMapSearchInput,
  debouncedSearchInput,
  searchResults,
  setSearchResults,
}: {
  selectedLocation: mapkit.Coordinate | undefined;
  handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
  index: number;
  mapSearchInput: string;
  setMapSearchInput: (e: string) => void;
  debouncedSearchInput: string;
  searchResults: mapkit.SearchAutocompleteResult[];
  setSearchResults: (e: mapkit.SearchAutocompleteResult[]) => void;
}) => {
  const MapMemo = useMemo(() => {
    return (
      <MapElement
        selectedLocation={selectedLocation}
        onLocationSelect={handleChangeLocation}
        label={' '}
      />
    );
  }, [selectedLocation, handleChangeLocation]);

  return (
    <div className="rounded-border-with-shadow absolute size-full grow bg-[#8CD4F5]">
      {MapMemo}
      <label
        htmlFor={`round_${index}_map_modal`}
        className={`absolute right-4 top-4 z-10 ${smallButtonClasses}`}
      >
        <FullscreenRoundedIcon htmlColor="black" />
      </label>
      <MapSearchInput
        handleChangeLocation={handleChangeLocation}
        mapSearchInput={mapSearchInput}
        setMapSearchInput={setMapSearchInput}
        debouncedSearchInput={debouncedSearchInput}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
      />
    </div>
  );
};

const MapSearchInput = ({
  mapSearchInput,
  setMapSearchInput,
  debouncedSearchInput,
  handleChangeLocation,
  searchResults,
  setSearchResults,
}: {
  mapSearchInput: string;
  setMapSearchInput: (e: string) => void;
  debouncedSearchInput: string;
  handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
  searchResults: mapkit.SearchAutocompleteResult[];
  setSearchResults: (e: mapkit.SearchAutocompleteResult[]) => void;
}) => {
  useEffect(() => {
    mapkitSearch(debouncedSearchInput, setSearchResults);
  }, [debouncedSearchInput]);

  return (
    <div className="absolute left-4 top-4 z-10 w-3/4">
      <MapkitAutocomplete
        items={searchResults}
        value={mapSearchInput}
        onChange={setMapSearchInput}
        onSelectLocation={handleChangeLocation}
      />
    </div>
  );
};

const YearInput = ({
  index,
  year,
  yearMessageHolder,
}: {
  index: number;
  year: string;
  yearMessageHolder: MessageHolderType;
}) => {
  const { setRoundData, loading } = useGameFormProvider();

  const updateYear = (value: string) => {
    if (isNaN(parseInt(value)) && value !== '') {
      return;
    }
    setRoundData({ year: value }, index);
  };

  const borderColor =
    yearMessageHolder.messageHolderProps.messageVisible &&
    yearMessageHolder.messageHolderProps.message &&
    yearMessageHolder.messageHolderProps.message.type === 'success'
      ? 'border-[#0AC265]'
      : 'border-[#D0D5DD]';

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row justify-between">
        <label
          htmlFor={`round_${index}_year_input`}
          className="mb-1 text-sm font-medium"
        >
          Year
        </label>
        <CompletedIcon completed={isYearInValidRange(year)} />
      </div>
      <input
        id={`round_${index}_year_input`}
        type="text"
        disabled={loading}
        inputMode="numeric"
        maxLength={4}
        placeholder="Year"
        className={`input input-md h-[44px] w-full rounded-md border ${borderColor} text-base focus:border-info-content focus:outline-none`}
        value={year}
        onChange={(e) => updateYear(e.target.value.trim())}
      />
    </div>
  );
};

const DescriptionInput = ({
  index,
  description,
}: {
  index: number;
  description: string;
}) => {
  const { setRoundData, loading } = useGameFormProvider();

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row justify-between">
        <label
          htmlFor={`round_${index}_description_input`}
          className="mb-1 text-sm font-medium"
        >
          Description
        </label>
        <CompletedIcon completed={!isEmpty(description)} />
      </div>
      <textarea
        id={`round_${index}_description_input`}
        disabled={loading}
        rows={4}
        placeholder="Description"
        className="input input-md h-[128px] w-full rounded-md border border-[#D0D5DD] py-2 text-base focus:border-[#D6BBFB] focus:outline-none"
        value={description}
        onChange={(e) => setRoundData({ description: e.target.value }, index)}
      />
    </div>
  );
};

const PhotographerInput = ({
  index,
  photographer,
}: {
  index: number;
  photographer?: string;
}) => {
  const { setRoundData, loading } = useGameFormProvider();

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row justify-between">
        <label
          htmlFor={`round_${index}_photographer_input`}
          className="mb-1 text-sm font-medium"
        >
          Photographer
        </label>
      </div>
      <input
        id={`round_${index}_photographer_input`}
        type="text"
        disabled={loading}
        placeholder="Photographer"
        className={`input input-md h-[44px] w-full rounded-md border border-[#D0D5DD] text-base focus:border-info-content focus:outline-none`}
        value={photographer}
        onChange={(e) => {
          if (e.target.value && e.target.value != '') {
            setRoundData({ photographer: e.target.value }, index);
          } else {
            setRoundData({ photographer: undefined }, index);
          }
        }}
      />
    </div>
  );
};

const ViewDetailsOrCollapseButton = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <div className="flex flex-row items-center gap-1 text-sm font-medium text-info-content">
      <span>{`${isOpen ? 'Collapse' : 'View Details'}`}</span>
      <div className={`duration-500 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
        <ExpandMoreRoundedIcon />
      </div>
    </div>
  );
};

export const RoundTag = ({
  index,
  dataValid,
}: {
  index: number;
  dataValid: boolean;
}) => {
  return (
    <div className="relative max-w-fit rounded-full bg-primary-content px-2 py-1 text-xs text-secondary">
      {`Round ${index + 1}`}
      {!dataValid && (
        <div className="absolute -top-0.5 right-0 z-10 size-2 rounded-full bg-[#DF2020]" />
      )}
    </div>
  );
};
